import { useSettingsQuery } from 'src/api/queries/userQueries';
import {
  AdminSettingsSection,
  Checkbox,
  ColorPicker,
  Input,
  PageBody,
  SaveChangesBar,
  Select,
  SettingsNavigation,
} from 'src/components';
import styles from './styles.module.scss';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import ReactSwitch from 'react-switch';
import { CaretDown, CaretRight } from 'src/common/icons';
import { VideoTranscodes } from './components/VideoTranscodes';
import { videoSettingsAtom } from 'src/shared/settings/video';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { ACTION_TYPES } from 'src/shared/settings/base';





type FormData = {
  authorized_domains: string;
  dropbox_app_key: string;
  dropbox_app_secret: string;
  dropbox_redirect_url: string;
};

export const VideoSettings = () => {
  const { isLoading } = useSettingsQuery();

  const [videoSettings, updateVideoSettings] = useAtom(videoSettingsAtom);
  const [addonsSettings] = useAtom(addonsSettingsAtom);

  const videoKeys = useMemo(() => (videoSettings ? Object.keys(videoSettings.transcodes) : []), [videoSettings]);

  const videoPreview = useMemo(() => videoSettings?.preview, [videoSettings]);

  const videoStoryBoard = useMemo(() => videoSettings?.thumbnails.storyboard, [videoSettings]);

  const [storyboardAdvanced, setStoryboardAdvanced] = useState(false);
  const [previewAdvanced, setPreviewAdvanced] = useState(false);

  const handleThumbnailUpdate = (name: string, value: number) =>
    updateVideoSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: { thumbnails: { video_default: { [name]: value } } },
    });

  const handleStoryboardUpdate = (name: string, value: string | boolean | number) =>
    updateVideoSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: { thumbnails: { storyboard: { [name]: value } } },
    });

  const handlePreviewUpdate = (name: string, value: any) =>
    updateVideoSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: { preview: { [name]: value } },
    });

  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.pageWrapper}>
      <SettingsNavigation />
      <SaveChangesBar />

      <PageBody narrow>
        <AdminSettingsSection title="Test transcode mode">
          <div className={styles.onDemandImages}>
            <ReactSwitch
              checked={videoSettings.video_transcode_test_mode}
              onChange={(checked: boolean) =>
                updateVideoSettings({
                  type: ACTION_TYPES.UPDATE_STATE,
                  payload: { video_transcode_test_mode: checked },
                })
              }
              checkedIcon={false}
              uncheckedIcon={false}
              onColor="#2e53e3"
            />

            <p>Limit all video transcoding to 5 seconds (Free)</p>
          </div>
        </AdminSettingsSection>
          <AdminSettingsSection title="Video thumbnail size">
            <div className={styles.formCard}>
              <div className={styles.formCardGrid}>
                <div className={styles.formCardField}>
                  <label>Width (PX)</label>
                  <Input
                    variant="secondary"
                    value={videoSettings.thumbnails.video_default.width}
                    type="number"
                    onChange={(value) => handleThumbnailUpdate('width', parseInt(value))}
                  />
                </div>
                <div className={styles.formCardField}>
                  <label>Height (PX)</label>
                  <Input
                    variant="secondary"
                    type="number"
                    value={videoSettings.thumbnails.video_default.height}
                    onChange={(value) => handleThumbnailUpdate('height', parseInt(value))}
                  />
                </div>
              </div>
            </div>
          </AdminSettingsSection>
          {videoSettings.thumbnails.storyboard && addonsSettings?.STORYBOARD?.enabled && (
            <AdminSettingsSection title="Storyboard images">
              <div className={styles.formCard}>
                <div className={styles.formCardHeader}>
                  <span>Create on upload</span>
                  <ReactSwitch
                    checked={videoSettings.thumbnails.storyboard.enabled}
                    onChange={(checked: boolean) => handleStoryboardUpdate('enabled', checked)}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onColor="#2e53e3"
                    width={40}
                    height={20}
                    handleDiameter={16}
                  />
                </div>
                <div className={styles.formCardGrid}>
                  <div className={styles.formCardField}>
                    <label>Type</label>
                    <Select
                      value={videoStoryBoard.mode}
                      onChange={(value: string) => handleStoryboardUpdate('mode', value)}
                      options={[
                        { label: 'Extract N images over entire video', value: 'number' },
                        { label: 'Extract image every Nth second', value: 'second' },
                        { label: 'Extract all Keyframes as images', value: 'keyframes' },
                      ]}
                    />
                  </div>
                  <div className={styles.formCardField}>
                    <label>Value</label>
                    <Input
                      variant="secondary"
                      value={videoSettings.thumbnails.storyboard.mode_value}
                      onChange={(value) => handleStoryboardUpdate('mode_value', value === '' ? 5 : parseInt(value))}
                    />
                  </div>
                </div>
                <div className={styles.formCardGrid} style={{ gridTemplateColumns: '1fr 1fr auto' }}>
                  <div className={styles.formCardField}>
                    <label>Width (PX)</label>
                    <Input
                      variant="secondary"
                      value={videoSettings.thumbnails.storyboard.width}
                      onChange={(value) => handleStoryboardUpdate('width', value === '' ? 1280 : parseInt(value))}
                    />
                  </div>
                  <div className={styles.formCardField}>
                    <label>Height (PX)</label>
                    <Input
                      variant="secondary"
                      value={videoSettings.thumbnails.storyboard.height}
                      onChange={(value) => handleStoryboardUpdate('height', value === '' ? 720 : parseInt(value))}
                    />
                  </div>
                  <div className={styles.formCardField}>
                    <label>Public</label>
                    <Checkbox
                      checked={videoSettings.thumbnails.storyboard.public}
                      onChange={(e) => handleStoryboardUpdate('public', e.target.checked)}
                    />
                  </div>
                </div>
                <div
                  className={styles.advancedToggle}
                  onClick={() => setStoryboardAdvanced((prev) => !prev)}
                >
                  {storyboardAdvanced ? <CaretDown /> : <CaretRight />}
                  <span>Advanced</span>
                </div>
                {storyboardAdvanced && (
                  <div className={styles.advancedContent}>
                    <div className={styles.formCardField}>
                      <label>Mode</label>
                      <Select
                        value={videoSettings.thumbnails.storyboard.aspect}
                        onChange={(value) => handleStoryboardUpdate('aspect', value)}
                        options={[
                          { label: 'Pad', value: 'pad' },
                          { label: 'Preserve', value: 'preserve' },
                          { label: 'Scale', value: 'scale' },
                          { label: 'Crop', value: 'crop' },
                        ]}
                      />
                    </div>
                    <div className={styles.formCardField}>
                      <label>Padding color</label>
                      <ColorPicker
                        value={videoSettings.thumbnails.storyboard.padding}
                        onChange={(color) => handleStoryboardUpdate('padding', color)}
                      />
                    </div>
                    <div className={styles.formCardField}>
                      <label>Upscale</label>
                      <Checkbox
                        checked={videoSettings.thumbnails.storyboard.upscale}
                        onChange={(e) => handleStoryboardUpdate('upscale', e.target.checked)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </AdminSettingsSection>
          )}
          <AdminSettingsSection title="Video Preview">
            <div className={styles.formCard}>
              <div className={styles.formCardHeader}>
                <span>Create on upload</span>
                <ReactSwitch
                  checked={videoPreview.video_preview_enabled}
                  onChange={(checked: boolean) => handlePreviewUpdate('video_preview_enabled', checked)}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#2e53e3"
                  width={40}
                  height={20}
                  handleDiameter={16}
                />
              </div>
              <div className={styles.formCardField} style={{ marginBottom: '1rem' }}>
                <label>Preset</label>
                <Select
                  value={videoSettings.preview.video_preview_preset}
                  onChange={(value: string) => handlePreviewUpdate('video_preview_preset', value)}
                  options={videoKeys.map((key) => ({ label: key, value: key }))}
                />
              </div>
              <div className={styles.formCardGrid} style={{ gridTemplateColumns: '1fr 1fr auto' }}>
                <div className={styles.formCardField}>
                  <label>Start at (second)</label>
                  <Input
                    variant="secondary"
                    value={videoPreview.video_preview_start}
                    onChange={(value: string) => handlePreviewUpdate('video_preview_start', value === '' ? 0 : parseInt(value))}
                  />
                </div>
                <div className={styles.formCardField}>
                  <label>Length (seconds)</label>
                  <Input
                    variant="secondary"
                    value={videoPreview.video_preview_length}
                    onChange={(value: string) => handlePreviewUpdate('video_preview_length', value === '' ? 0 : parseInt(value))}
                  />
                </div>
                <div className={styles.formCardField}>
                  <label>Public</label>
                  <Checkbox
                    checked={videoPreview.video_preview_public}
                    onChange={(e) => handlePreviewUpdate('video_preview_public', e.target.checked)}
                  />
                </div>
              </div>
              <div
                className={styles.advancedToggle}
                onClick={() => setPreviewAdvanced((prev) => !prev)}
              >
                {previewAdvanced ? <CaretDown /> : <CaretRight />}
                <span>Advanced</span>
              </div>
              {previewAdvanced && (
                <div className={styles.advancedContent}>
                  <div className={styles.formCardField}>
                    <label>Mode</label>
                    <Select
                      value={videoPreview.video_preview_aspect}
                      onChange={(value: string) => handlePreviewUpdate('video_preview_aspect', value)}
                      options={[
                        { label: 'Pad', value: 'pad' },
                        { label: 'Preserve', value: 'preserve' },
                        { label: 'Scale', value: 'scale' },
                        { label: 'Crop', value: 'crop' },
                      ]}
                    />
                  </div>
                  <div className={styles.formCardField}>
                    <label>Padding color</label>
                    <ColorPicker
                      value={videoPreview.video_preview_padding}
                      onChange={(color) => handlePreviewUpdate('video_preview_padding', color)}
                    />
                  </div>
                  <div className={styles.formCardField}>
                    <label>Upscale</label>
                    <Checkbox
                      checked={videoPreview.video_preview_upscale}
                      onChange={(e) => handlePreviewUpdate('video_preview_upscale', e.target.checked)}
                    />
                  </div>
                </div>
              )}
            </div>
        </AdminSettingsSection>
        <VideoTranscodes />
      </PageBody>
    </div>
  );
};
